import type { WheelDateValue } from '../components/WheelDatePicker';
import {
  pneuDraftFromApi,
  savePneuCheckupProgress,
  type WorkDustHistoryProgressPayload,
} from './careerHistorySurveyApi';
import {
  clearPneuDraftFromStep,
  committedPneuStepsForDraft,
  isPneuStep,
  nextPneuStepAfter,
  PNEU_FIELD_STEPS,
  PNEU_GATE_STEP,
  PNEU_SECTION_COMPLETE_STEP,
  prevPneuStepForUndo,
  type PneuCheckupStep,
} from './careerHistorySurveyPneuSteps';
import type { CareerHistorySurveyStep, PneuCheckupDraft } from './careerHistorySurveyTypes';

function formatIsoDate(iso: string, locale: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function userAnswerForPneuStep(
  s: PneuCheckupStep,
  draft: PneuCheckupDraft,
  t: (key: string) => string,
  locale: string
): string {
  const yes = t('submitDocuments.careerHistorySurvey.yes');
  const no = t('submitDocuments.careerHistorySurvey.no');
  switch (s) {
    case 'pneuGate':
      return draft.gateAnswer === 'yes' ? yes : draft.gateAnswer === 'no' ? no : '';
    case 'pneu1Date':
      return draft.slot1.checkupDate ? formatIsoDate(draft.slot1.checkupDate, locale) : '';
    case 'pneu1Contractor':
      return draft.slot1.contractor.trim();
    case 'pneu1Findings':
      return draft.slot1.findings === 'yes' ? yes : draft.slot1.findings === 'no' ? no : '';
    case 'pneu1Area':
      return draft.slot1.area.trim();
    case 'pneu2Date':
      if (draft.slot2Skipped) return t('submitDocuments.careerHistorySurvey.pneuSlot2Skip');
      return draft.slot2.checkupDate ? formatIsoDate(draft.slot2.checkupDate, locale) : '';
    case 'pneu2Contractor':
      if (draft.slot2Skipped) return t('submitDocuments.careerHistorySurvey.pneuSlot2Skip');
      return draft.slot2.contractor.trim();
    case 'pneu2Findings':
      if (draft.slot2Skipped) return t('submitDocuments.careerHistorySurvey.pneuSlot2Skip');
      return draft.slot2.findings === 'yes' ? yes : draft.slot2.findings === 'no' ? no : '';
    case 'pneu2Area':
      if (draft.slot2Skipped) return t('submitDocuments.careerHistorySurvey.pneuSlot2Skip');
      return draft.slot2.area.trim();
    default:
      return '';
  }
}

export function applyPneuFromProgress(progress: WorkDustHistoryProgressPayload | null): {
  draft: PneuCheckupDraft;
  committed: Set<PneuCheckupStep>;
  gateCommitted: boolean;
} {
  const draft = pneuDraftFromApi(progress?.pneuCheckup, progress?.pneuCheckups, {
    sectionPneuDone: progress?.sectionPneuDone,
    currentStepId: progress?.currentStepId,
  });
  const committed = committedPneuStepsForDraft(draft);
  const gateCommitted =
    (progress?.pneuCheckup === true || progress?.pneuCheckup === false) &&
    progress?.currentStepId?.trim() !== PNEU_GATE_STEP;
  return { draft, committed, gateCommitted };
}

export async function persistPneuProgress(params: {
  email: string;
  invitationId?: string;
  draft: PneuCheckupDraft;
  nextStep: CareerHistorySurveyStep;
  sectionPneuDone?: boolean;
}): Promise<WorkDustHistoryProgressPayload | null> {
  return savePneuCheckupProgress({
    email: params.email,
    invitationId: params.invitationId,
    draft: params.draft,
    currentStepId: params.nextStep,
    sectionPneuDone: params.sectionPneuDone,
  });
}

export {
  clearPneuDraftFromStep,
  committedPneuStepsForDraft,
  isPneuStep,
  nextPneuStepAfter,
  PNEU_FIELD_STEPS,
  PNEU_GATE_STEP,
  PNEU_SECTION_COMPLETE_STEP,
  prevPneuStepForUndo,
  type PneuCheckupStep,
};

export type { WheelDateValue };
